import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { Add, Edit, Delete, Search, Business, People, Work, Security } from '@mui/icons-material';
import { apiService } from '../../services/api';
import { COUNTRIES, CURRENCIES } from '../../data/constants';
import AccessControl from './AccessControl';

interface Entity {
  id: string;
  code: string;
  name: string;
  country: string;
  registrationId?: string;
  createdAt: string;
}

interface Department {
  id: string;
  name: string;
  description?: string;
  entity: Entity;
  createdAt: string;
}

interface Position {
  id: string;
  title: string;
  level?: string;
  employmentTypeDefault?: string;
  standardHoursPerWeek?: number;
  defaultProbationMonths?: number;
  description?: string;
  entity: Entity;
  department?: Department;
  createdAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Admin: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Confirm delete dialog state
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; type: string; id: string; name: string }>({ open: false, type: '', id: '', name: '' });

  // Modal states
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [isEntityModalOpen, setIsEntityModalOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  
  // Form states
  const [deptFormData, setDeptFormData] = useState({
    name: '',
    description: '',
    entityId: '',
  });
  
  const [entityFormData, setEntityFormData] = useState({
    code: '',
    name: '',
    country: '',
    registrationId: '',
  });

  const [positionFormData, setPositionFormData] = useState({
    title: '',
    level: '',
    employmentTypeDefault: '',
    standardHoursPerWeek: 40,
    defaultProbationMonths: 3,
    description: '',
    entityId: '',
    departmentId: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [entitiesData, deptData, positionsData] = await Promise.all([
        apiService.getEntities(),
        apiService.getDepartments(),
        apiService.getPositions()
      ]);
      setEntities([...entitiesData].sort((a, b) => a.code.localeCompare(b.code)));
      setDepartments(deptData);
      setPositions(positionsData);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchTerm('');
    setError('');
  };

  // Entity management
  const handleOpenEntityModal = (entity?: Entity) => {
    if (entity) {
      setEditingEntity(entity);
      setEntityFormData({
        code: entity.code,
        name: entity.name,
        country: entity.country,
        registrationId: entity.registrationId || '',
      });
    } else {
      setEditingEntity(null);
      setEntityFormData({
        code: '',
        name: '',
        country: '',
        registrationId: '',
      });
    }
    setIsEntityModalOpen(true);
  };

  const handleCloseEntityModal = () => {
    setIsEntityModalOpen(false);
    setEditingEntity(null);
    setEntityFormData({
      code: '',
      name: '',
      country: '',
      registrationId: '',
    });
  };

  const handleEntityInputChange = (field: string, value: string) => {
    setEntityFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEntitySubmit = async () => {
    try {
      setError('');
      if (editingEntity) {
        await apiService.updateEntity(editingEntity.id, entityFormData);
      } else {
        await apiService.createEntity(entityFormData);
      }
      handleCloseEntityModal();
      fetchData();
    } catch (err: any) {
      console.error('Error saving entity:', err);
      setError(err.response?.data?.message || 'Failed to save entity');
    }
  };

  const handleDeleteEntity = (id: string) => {
    const entity = entities.find(e => e.id === id);
    setConfirmDelete({ open: true, type: 'entity', id, name: entity?.name || '' });
  };

  // Department management
  const handleOpenDeptModal = (department?: Department) => {
    if (department) {
      setEditingDepartment(department);
      setDeptFormData({
        name: department.name,
        description: department.description || '',
        entityId: department.entity.id,
      });
    } else {
      setEditingDepartment(null);
      setDeptFormData({
        name: '',
        description: '',
        entityId: '',
      });
    }
    setIsDeptModalOpen(true);
  };

  const handleCloseDeptModal = () => {
    setIsDeptModalOpen(false);
    setEditingDepartment(null);
    setDeptFormData({
      name: '',
      description: '',
      entityId: '',
    });
  };

  const handleDeptInputChange = (field: string, value: string) => {
    setDeptFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDeptSubmit = async () => {
    try {
      setError('');
      if (editingDepartment) {
        await apiService.updateDepartment(editingDepartment.id, deptFormData);
      } else {
        await apiService.createDepartment(deptFormData);
      }
      handleCloseDeptModal();
      fetchData();
    } catch (err: any) {
      console.error('Error saving department:', err);
      setError(err.response?.data?.message || 'Failed to save department');
    }
  };

  const handleDeleteDepartment = (id: string) => {
    const dept = departments.find(d => d.id === id);
    setConfirmDelete({ open: true, type: 'department', id, name: dept?.name || '' });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = confirmDelete;
    setConfirmDelete({ open: false, type: '', id: '', name: '' });
    try {
      if (type === 'department') await apiService.deleteDepartment(id);
      else if (type === 'entity') await apiService.deleteEntity(id);
      else if (type === 'position') await apiService.deletePosition(id);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to delete ${type}`);
    }
  };

  // Position management
  const handleOpenPositionModal = (position?: Position) => {
    if (position) {
      setEditingPosition(position);
      setPositionFormData({
        title: position.title,
        level: position.level || '',
        employmentTypeDefault: position.employmentTypeDefault || '',
        standardHoursPerWeek: position.standardHoursPerWeek || 40,
        defaultProbationMonths: position.defaultProbationMonths || 3,
        description: position.description || '',
        entityId: position.entity.id,
        departmentId: position.department?.id || '',
      });
    } else {
      setEditingPosition(null);
      setPositionFormData({
        title: '',
        level: '',
        employmentTypeDefault: '',
        standardHoursPerWeek: 40,
        defaultProbationMonths: 3,
        description: '',
        entityId: '',
        departmentId: '',
      });
    }
    setIsPositionModalOpen(true);
  };

  const handleClosePositionModal = () => {
    setIsPositionModalOpen(false);
    setEditingPosition(null);
    setPositionFormData({
      title: '',
      level: '',
      employmentTypeDefault: '',
      standardHoursPerWeek: 40,
      defaultProbationMonths: 3,
      description: '',
      entityId: '',
      departmentId: '',
    });
  };

  const handlePositionInputChange = (field: string, value: string | number) => {
    setPositionFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePositionSubmit = async () => {
    try {
      setError('');
      if (editingPosition) {
        await apiService.updatePosition(editingPosition.id, positionFormData);
      } else {
        await apiService.createPosition(positionFormData);
      }
      handleClosePositionModal();
      fetchData();
    } catch (err: any) {
      console.error('Error saving position:', err);
      setError(err.response?.data?.message || 'Failed to save position');
    }
  };

  const handleDeletePosition = (id: string) => {
    const pos = positions.find(p => p.id === id);
    setConfirmDelete({ open: true, type: 'position', id, name: pos?.title || '' });
  };

  // Filtering
  const filteredEntities = entities.filter(
    (entity) =>
      entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDepartments = departments.filter(
    (dept) =>
      dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.entity?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dept.description && dept.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredPositions = positions.filter(
    (pos) =>
      pos.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pos.entity?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pos.department?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pos.level && pos.level.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (pos.description && pos.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const isEntityFormValid = entityFormData.code && /^(0[0-9]{2}|[1-9][0-9]?|100)$/.test(entityFormData.code) && entityFormData.name && entityFormData.country;
  const isDeptFormValid = deptFormData.name && deptFormData.entityId;
  const isPositionFormValid = positionFormData.title && positionFormData.entityId;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Admin Panel</Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
          <Tab icon={<Business />} label="Entities" />
          <Tab icon={<People />} label="Departments" />
          <Tab icon={<Work />} label="Positions" />
          <Tab icon={<Security />} label="Access Control" />
        </Tabs>

        {/* Entities Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Manage Entities</Typography>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => handleOpenEntityModal()}
            >
              Add Entity
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search entities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Registration ID</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntities.map((entity) => (
                  <TableRow key={entity.id} hover>
                    <TableCell>
                      <Chip label={entity.code} size="small" />
                    </TableCell>
                    <TableCell>{entity.name}</TableCell>
                    <TableCell>{entity.country}</TableCell>
                    <TableCell>{entity.registrationId || '-'}</TableCell>
                    <TableCell>
                      {entity.createdAt ? new Date(entity.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEntityModal(entity)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntity(entity.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredEntities.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">
                        {searchTerm ? 'No entities found matching your search.' : 'No entities found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Total: {filteredEntities.length} entities
              {searchTerm && ` (filtered from ${entities.length})`}
            </Typography>
          </Box>
        </TabPanel>

        {/* Departments Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Manage Departments</Typography>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => handleOpenDeptModal()}
            >
              Add Department
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDepartments.map((department) => (
                  <TableRow key={department.id} hover>
                    <TableCell>{department.name}</TableCell>
                    <TableCell>{department.description || '-'}</TableCell>
                    <TableCell>{department.entity?.name || '-'}</TableCell>
                    <TableCell>
                      {department.createdAt ? new Date(department.createdAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeptModal(department)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDepartment(department.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredDepartments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">
                        {searchTerm ? 'No departments found matching your search.' : 'No departments found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Total: {filteredDepartments.length} departments
              {searchTerm && ` (filtered from ${departments.length})`}
            </Typography>
          </Box>
        </TabPanel>

        {/* Positions Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Manage Positions</Typography>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => handleOpenPositionModal()}
            >
              Add Position
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Employment Type</TableCell>
                  <TableCell>Hours/Week</TableCell>
                  <TableCell>Probation (Months)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPositions.map((position) => (
                  <TableRow key={position.id} hover>
                    <TableCell>{position.title}</TableCell>
                    <TableCell>{position.level || '-'}</TableCell>
                    <TableCell>{position.entity?.name || '-'}</TableCell>
                    <TableCell>{position.department?.name || '-'}</TableCell>
                    <TableCell>{position.employmentTypeDefault || '-'}</TableCell>
                    <TableCell>{position.standardHoursPerWeek || '-'}</TableCell>
                    <TableCell>{position.defaultProbationMonths || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPositionModal(position)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePosition(position.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredPositions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">
                        {searchTerm ? 'No positions found matching your search.' : 'No positions found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Total: {filteredPositions.length} positions
              {searchTerm && ` (filtered from ${positions.length})`}
            </Typography>
          </Box>
        </TabPanel>

        {/* Access Control Tab */}
        <TabPanel value={tabValue} index={3}>
          <AccessControl />
        </TabPanel>
      </Paper>

      {/* Entity Modal */}
      <Dialog open={isEntityModalOpen} onClose={handleCloseEntityModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEntity ? 'Edit Entity' : 'Add New Entity'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Entity Code"
                value={entityFormData.code}
                onChange={(e) => handleEntityInputChange('code', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                required
                inputProps={{ maxLength: 3 }}
                helperText="3-digit code: 001 – 100"
                error={!!entityFormData.code && !/^(0[0-9]{2}|[1-9][0-9]?|100)$/.test(entityFormData.code)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Entity Name"
                value={entityFormData.name}
                onChange={(e) => handleEntityInputChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={COUNTRIES}
                getOptionLabel={(option) => option.name}
                value={COUNTRIES.find(c => c.name === entityFormData.country) || null}
                onChange={(event, newValue) => {
                  handleEntityInputChange('country', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    required
                    helperText="Select country"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Registration ID"
                value={entityFormData.registrationId}
                onChange={(e) => handleEntityInputChange('registrationId', e.target.value)}
                helperText="Government registration number (optional)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEntityModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEntitySubmit}
            disabled={!isEntityFormValid}
          >
            {editingEntity ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Department Modal */}
      <Dialog open={isDeptModalOpen} onClose={handleCloseDeptModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDepartment ? 'Edit Department' : 'Add New Department'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Department Name"
                value={deptFormData.name}
                onChange={(e) => handleDeptInputChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={deptFormData.description}
                onChange={(e) => handleDeptInputChange('description', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Entity</InputLabel>
                <Select
                  value={deptFormData.entityId}
                  onChange={(e) => handleDeptInputChange('entityId', e.target.value)}
                  label="Entity"
                >
                  {entities.map((entity) => (
                    <MenuItem key={entity.id} value={entity.id}>
                      {entity.name} ({entity.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeptModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDeptSubmit}
            disabled={!isDeptFormValid}
          >
            {editingDepartment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Position Modal */}
      <Dialog open={isPositionModalOpen} onClose={handleClosePositionModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPosition ? 'Edit Position' : 'Add New Position'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Position Title"
                value={positionFormData.title}
                onChange={(e) => handlePositionInputChange('title', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Level"
                value={positionFormData.level}
                onChange={(e) => handlePositionInputChange('level', e.target.value)}
                helperText="e.g., Junior, Senior, Manager"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Entity</InputLabel>
                <Select
                  value={positionFormData.entityId}
                  onChange={(e) => handlePositionInputChange('entityId', e.target.value)}
                  label="Entity"
                >
                  {entities.map((entity) => (
                    <MenuItem key={entity.id} value={entity.id}>
                      {entity.name} ({entity.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={positionFormData.departmentId}
                  onChange={(e) => handlePositionInputChange('departmentId', e.target.value)}
                  label="Department"
                  disabled={!positionFormData.entityId}
                >
                  <MenuItem value="">
                    <em>No Department</em>
                  </MenuItem>
                  {departments
                    .filter(dept => dept.entity.id === positionFormData.entityId)
                    .map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={positionFormData.employmentTypeDefault}
                  onChange={(e) => handlePositionInputChange('employmentTypeDefault', e.target.value)}
                  label="Employment Type"
                >
                  <MenuItem value="">
                    <em>Not Specified</em>
                  </MenuItem>
                  <MenuItem value="full_time">Full Time</MenuItem>
                  <MenuItem value="part_time">Part Time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="temporary">Temporary</MenuItem>
                  <MenuItem value="intern">Intern</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Standard Hours Per Week"
                type="number"
                value={positionFormData.standardHoursPerWeek}
                onChange={(e) => handlePositionInputChange('standardHoursPerWeek', Number(e.target.value))}
                inputProps={{ min: 0, max: 168 }}
                helperText="Default working hours per week"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Default Probation Period (Months)"
                type="number"
                value={positionFormData.defaultProbationMonths}
                onChange={(e) => handlePositionInputChange('defaultProbationMonths', Number(e.target.value))}
                inputProps={{ min: 0, max: 24 }}
                helperText="Probation period in months"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={positionFormData.description}
                onChange={(e) => handlePositionInputChange('description', e.target.value)}
                helperText="Job description and responsibilities"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePositionModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePositionSubmit}
            disabled={!isPositionFormValid}
          >
            {editingPosition ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, type: '', id: '', name: '' })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, type: '', id: '', name: '' })}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;